package com.patronat.top.client.presentation.home

import androidx.lifecycle.viewModelScope
import com.patronat.top.core.auth.TokenStorage
import com.patronat.top.client.domain.location.ReverseClientLocationUseCase
import com.patronat.top.client.domain.location.SearchClientLocationsCommand
import com.patronat.top.client.domain.location.SearchClientLocationsUseCase
import com.patronat.top.client.domain.reviews.CreateClientReviewCommand
import com.patronat.top.client.domain.reviews.CreateClientReviewUseCase
import com.patronat.top.client.domain.reviews.GetClientVenueReviewsCommand
import com.patronat.top.client.domain.reviews.GetClientReviewSummaryUseCase
import com.patronat.top.client.domain.reviews.GetClientVenueReviewsUseCase
import com.patronat.top.client.domain.reviews.ToggleClientReviewLikeCommand
import com.patronat.top.client.domain.reviews.ToggleClientReviewLikeUseCase
import com.patronat.top.client.domain.venues.GetClientVenueByIdUseCase
import com.patronat.top.client.domain.venues.GetClientVenueServiceSuggestionsUseCase
import com.patronat.top.client.domain.venues.GetClientSavedVenueIdsUseCase
import com.patronat.top.client.domain.venues.GetClientVenuesUseCase
import com.patronat.top.client.domain.venues.ToggleClientSavedVenueCommand
import com.patronat.top.client.domain.venues.ToggleClientSavedVenueUseCase
import com.patronat.top.client.location.ClientGeoLocation
import com.patronat.top.client.location.ClientLocationSuggestion
import com.patronat.top.client.location.ClientLocationProvider
import com.patronat.top.client.presentation.home.model.HomeCategoryShortcut
import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.domain.models.DomainResult
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.ImageType
import com.patronat.top.dto.ReviewMediaRef
import com.patronat.top.dto.ReviewResponse
import com.patronat.top.dto.ReviewRatingDetails
import com.patronat.top.dto.VenueFilterRequest
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.VenueCategory
import com.patronat.top.dto.venues.VenueGroup
import com.patronat.top.dto.venues.VenueStatus
import com.patronat.top.library.media.domain.command.UploadImageCommand
import com.patronat.top.library.media.domain.usecase.UploadImageUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.PI
import kotlin.math.atan2
import kotlin.math.ceil
import kotlin.math.cos
import kotlin.math.floor
import kotlin.math.pow
import kotlin.math.roundToInt
import kotlin.math.sin
import kotlin.math.sqrt
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_home_load_error_title
import top.clientapp.generated.resources.client_review_auth_required_message
import top.clientapp.generated.resources.client_review_auth_required_title
import top.clientapp.generated.resources.client_review_photo_upload_error
import top.clientapp.generated.resources.client_review_submit_error
import top.clientapp.generated.resources.client_review_submit_success
import top.clientapp.generated.resources.client_review_submit_success_message
import top.clientapp.generated.resources.client_saved_action_error
import top.clientapp.generated.resources.client_saved_auth_required_message
import top.clientapp.generated.resources.client_saved_auth_required_title
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.error_alert_title

class ClientHomeViewModel(
    private val alertController: AlertController,
    private val getClientVenuesUseCase: GetClientVenuesUseCase,
    private val getVenueByIdUseCase: GetClientVenueByIdUseCase,
    private val getServiceSuggestionsUseCase: GetClientVenueServiceSuggestionsUseCase,
    private val getSavedVenueIdsUseCase: GetClientSavedVenueIdsUseCase,
    private val toggleSavedVenueUseCase: ToggleClientSavedVenueUseCase,
    private val createReviewUseCase: CreateClientReviewUseCase,
    private val getVenueReviewsUseCase: GetClientVenueReviewsUseCase,
    private val getReviewSummaryUseCase: GetClientReviewSummaryUseCase,
    private val toggleReviewLikeUseCase: ToggleClientReviewLikeUseCase,
    private val uploadImageUseCase: UploadImageUseCase,
    private val tokenStorage: TokenStorage,
    private val locationProvider: ClientLocationProvider,
    private val searchLocationsUseCase: SearchClientLocationsUseCase,
    private val reverseLocationUseCase: ReverseClientLocationUseCase
) : BaseViewModel<ClientHomeViewState, ClientHomeEvent, ClientHomeEffect>(
    initialState = ClientHomeViewState()
) {
    private var searchJob: Job? = null
    private var serviceSearchJob: Job? = null
    private var locationSearchJob: Job? = null

    init {
        loadSavedVenueIds()
        refreshLocationAndLoadVenues()
    }

    override fun onEvent(event: ClientHomeEvent) {
        when (event) {
            ClientHomeEvent.OnRefresh -> {
                loadSavedVenueIds()
                if (currentState.currentScreen == ClientHomeScreen.VenueReviews) {
                    currentState.selectedVenueId?.let(::loadVenueReviews)
                } else {
                    refreshLocationAndLoadVenues()
                }
            }
            is ClientHomeEvent.OnSearchChanged -> {
                updateState { it.copy(searchQuery = event.value) }
                searchJob?.cancel()
                searchJob = viewModelScope.launch {
                    delay(SearchDebounceMillis)
                    loadVenues()
                }
            }

            is ClientHomeEvent.OnHomeCategoryClicked -> {
                applyHomeCategory(event.category)
            }

            ClientHomeEvent.OnLocationSelectorClicked -> {
                updateState {
                    it.copy(
                        currentScreen = ClientHomeScreen.LocationSearch,
                        locationSearchQuery = "",
                        locationSuggestions = emptyList(),
                        isLocationSearchLoading = false
                    )
                }
            }

            ClientHomeEvent.OnCloseLocationSearchClicked -> {
                locationSearchJob?.cancel()
                updateState { it.copy(currentScreen = ClientHomeScreen.Feed) }
            }

            is ClientHomeEvent.OnLocationSearchChanged -> {
                updateState { it.copy(locationSearchQuery = event.value) }
                locationSearchJob?.cancel()
                if (event.value.length > LocationSearchMinLength) {
                    locationSearchJob = viewModelScope.launch {
                        delay(SearchDebounceMillis)
                        searchLocations(event.value)
                    }
                } else {
                    updateState {
                        it.copy(
                            locationSuggestions = emptyList(),
                            isLocationSearchLoading = false
                        )
                    }
                }
            }

            is ClientHomeEvent.OnLocationSuggestionClicked -> {
                locationSearchJob?.cancel()
                updateState {
                    it.copy(
                        currentScreen = ClientHomeScreen.Feed,
                        selectedSessionLocation = event.value,
                        currentLocationTitle = event.value.title,
                        locationSearchQuery = event.value.placeName,
                        locationSuggestions = emptyList(),
                        isLocationSearchLoading = false
                    )
                }
                loadVenues()
            }

            ClientHomeEvent.OnFilterClicked -> {
                updateState { it.copy(currentScreen = ClientHomeScreen.Filters) }
                loadServiceSuggestions(currentState.serviceSearchQuery)
            }

            ClientHomeEvent.OnCloseFiltersClicked -> {
                updateState { it.copy(currentScreen = ClientHomeScreen.Feed) }
            }

            is ClientHomeEvent.OnFilterLocationClicked -> {
                updateState { it.copy(filterLocation = event.value) }
            }

            is ClientHomeEvent.OnFilterPriceRangeChanged -> {
                updateState { state ->
                    val nextRange = event.value.coerceToBounds(state.filterPriceBounds)
                    state.copy(
                        filterPriceRange = nextRange,
                        isPriceFilterChanged = true
                    )
                }
            }

            is ClientHomeEvent.OnFilterRatingRangeChanged -> {
                updateState { it.copy(filterRatingRange = event.value) }
            }

            is ClientHomeEvent.OnServiceSearchChanged -> {
                updateState { it.copy(serviceSearchQuery = event.value) }
                serviceSearchJob?.cancel()
                serviceSearchJob = viewModelScope.launch {
                    delay(SearchDebounceMillis)
                    loadServiceSuggestions(event.value)
                }
            }

            is ClientHomeEvent.OnServiceSuggestionClicked -> {
                val service = event.value.trim()
                if (service.isNotBlank()) {
                    updateState { state ->
                        state.copy(
                            selectedServiceQueries = (state.selectedServiceQueries + service).distinctBy { it.lowercase() },
                            serviceSuggestions = state.serviceSuggestions.filterNot { suggestion ->
                                suggestion.equals(service, ignoreCase = true)
                            }
                        )
                    }
                }
            }

            is ClientHomeEvent.OnSelectedServiceRemoved -> {
                updateState { state ->
                    state.copy(
                        selectedServiceQueries = state.selectedServiceQueries.filterNot { service ->
                            service.equals(event.value, ignoreCase = true)
                        }
                    )
                }
                loadServiceSuggestions(currentState.serviceSearchQuery)
            }

            ClientHomeEvent.OnApplyFiltersClicked -> {
                updateState { state ->
                    state.copy(
                        currentScreen = ClientHomeScreen.Feed,
                        isFilterApplied = true,
                        venues = state.allVenues
                            .filteredBy(
                                group = state.selectedGroup,
                                category = state.selectedCategory
                            )
                            .sortByDistanceIfPossible(state.effectiveLocation())
                    )
                }
                loadVenues()
            }

            ClientHomeEvent.OnViewAllCategoriesClicked -> {
                updateState { it.copy(currentScreen = ClientHomeScreen.AllCategories) }
            }

            ClientHomeEvent.OnBackToHomeClicked -> {
                updateState { it.copy(currentScreen = ClientHomeScreen.Feed) }
            }

            is ClientHomeEvent.OnCategorySearchChanged -> {
                updateState { it.copy(categorySearchQuery = event.value) }
            }

            is ClientHomeEvent.OnCategoryGroupClicked -> {
                updateState { state ->
                    val expandedGroups = if (event.group in state.expandedCategoryGroups) {
                        state.expandedCategoryGroups - event.group
                    } else {
                        state.expandedCategoryGroups + event.group
                    }
                    state.copy(expandedCategoryGroups = expandedGroups)
                }
            }

            is ClientHomeEvent.OnVenueCategoryClicked -> {
                updateState { state ->
                    val selectedGroup = event.category.group
                    state.copy(
                        currentScreen = ClientHomeScreen.Feed,
                        selectedGroup = selectedGroup,
                        selectedCategory = event.category,
                        isFilterApplied = false,
                        venues = state.allVenues
                            .filteredBy(
                                group = selectedGroup,
                                category = event.category
                            )
                            .sortByDistanceIfPossible(state.effectiveLocation())
                    )
                }
                loadVenues()
            }

            is ClientHomeEvent.OnVenueClicked -> {
                openVenueDetails(event.venueId)
            }

            is ClientHomeEvent.OnVenueDeepLinkOpened -> {
                openVenueDetails(event.venueId)
            }

            ClientHomeEvent.OnVenueDetailsBackClicked -> {
                updateState {
                    it.copy(
                        currentScreen = ClientHomeScreen.Feed,
                        selectedVenueId = null
                    )
                }
            }

            ClientHomeEvent.OnVenueCatalogClicked -> {
                updateState { it.copy(currentScreen = ClientHomeScreen.VenueServices) }
            }

            ClientHomeEvent.OnVenueCatalogBackClicked -> {
                updateState { it.copy(currentScreen = ClientHomeScreen.VenueDetails) }
            }

            ClientHomeEvent.OnVenueReviewsClicked -> {
                val venueId = currentState.selectedVenueId ?: return
                updateState {
                    it.copy(
                        currentScreen = ClientHomeScreen.VenueReviews,
                        venueReviews = emptyList(),
                        venueReviewSummary = null,
                        selectedReviewPhotoUrl = null
                    )
                }
                loadVenueReviews(venueId)
            }

            ClientHomeEvent.OnVenueReviewsBackClicked -> {
                updateState {
                    it.copy(
                        currentScreen = ClientHomeScreen.VenueDetails,
                        selectedReviewPhotoUrl = null
                    )
                }
            }

            is ClientHomeEvent.OnVenueReviewPhotoClicked -> {
                updateState { it.copy(selectedReviewPhotoUrl = event.url) }
            }

            ClientHomeEvent.OnVenueReviewPhotoDismissed -> {
                updateState { it.copy(selectedReviewPhotoUrl = null) }
            }

            is ClientHomeEvent.OnVenueReviewLikeClicked -> {
                toggleReviewLike(event.reviewId)
            }

            is ClientHomeEvent.OnFavoriteClicked -> {
                toggleFavoriteVenue(event.venueId)
            }

            ClientHomeEvent.OnVenueReviewClicked -> {
                requireClientSession {
                    updateState {
                        it.copy(
                            currentScreen = ClientHomeScreen.VenueReview,
                            reviewForm = ClientReviewFormState()
                        )
                    }
                }
            }

            ClientHomeEvent.OnVenueReviewBackClicked -> {
                updateState { it.copy(currentScreen = ClientHomeScreen.VenueDetails) }
            }

            is ClientHomeEvent.OnReviewOverallRatingChanged -> {
                updateState { state ->
                    state.copy(
                        reviewForm = state.reviewForm.copy(
                            overallRating = event.value.coerceIn(MinReviewRating, MaxReviewRating)
                        )
                    )
                }
            }

            is ClientHomeEvent.OnReviewDetailRatingChanged -> {
                updateState { state ->
                    state.copy(reviewForm = state.reviewForm.withMetric(event.metric, event.value))
                }
            }

            is ClientHomeEvent.OnReviewTextChanged -> {
                updateState { state ->
                    state.copy(
                        reviewForm = state.reviewForm.copy(
                            text = event.value.take(MaxReviewTextLength)
                        )
                    )
                }
            }

            ClientHomeEvent.OnReviewPhotoAddClicked -> {
                requireClientSession {
                    sendEffect(ClientHomeEffect.OpenReviewPhotoPicker)
                }
            }

            is ClientHomeEvent.OnReviewPhotoPicked -> {
                uploadReviewPhoto(event.file)
            }

            is ClientHomeEvent.OnReviewPhotoRemoved -> {
                updateState { state ->
                    state.copy(
                        reviewForm = state.reviewForm.copy(
                            photos = state.reviewForm.photos.filterNot { it.mediaId == event.mediaId }
                        )
                    )
                }
            }

            ClientHomeEvent.OnSubmitReviewClicked -> submitReview()
        }
    }

    private fun loadSavedVenueIds() {
        viewModelScope.launch {
            val hasToken = !tokenStorage.getAccessToken().isNullOrBlank()
            if (!hasToken) {
                updateState { it.copy(favoriteVenueIds = emptySet()) }
                return@launch
            }

            when (val result = getSavedVenueIdsUseCase.execute(Unit)) {
                is DomainResult.Success -> {
                    updateState { it.copy(favoriteVenueIds = result.data.ids.toSet()) }
                }
                is DomainResult.Error -> Unit
            }
        }
    }

    private fun toggleFavoriteVenue(venueId: String) {
        viewModelScope.launch {
            val hasToken = !tokenStorage.getAccessToken().isNullOrBlank()
            if (!hasToken) {
                alertController.show(
                    title = UiText.StringResource(Res.string.client_saved_auth_required_title),
                    message = UiText.StringResource(Res.string.client_saved_auth_required_message),
                    type = NotificationType.WARNING
                )
                return@launch
            }

            val wasFavorite = venueId in currentState.favoriteVenueIds
            val nextFavoriteIds = if (wasFavorite) {
                currentState.favoriteVenueIds - venueId
            } else {
                currentState.favoriteVenueIds + venueId
            }

            updateState { it.copy(favoriteVenueIds = nextFavoriteIds) }

            when (
                val result = toggleSavedVenueUseCase.execute(
                    ToggleClientSavedVenueCommand(
                        venueId = venueId,
                        isSaved = wasFavorite
                    )
                )
            ) {
                is DomainResult.Success -> Unit
                is DomainResult.Error -> {
                    updateState { state ->
                        val restoredIds = if (wasFavorite) {
                            state.favoriteVenueIds + venueId
                        } else {
                            state.favoriteVenueIds - venueId
                        }
                        state.copy(favoriteVenueIds = restoredIds)
                    }
                    alertController.show(
                        title = UiText.StringResource(DesignRes.string.error_alert_title),
                        message = result.message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_saved_action_error),
                        type = NotificationType.ERROR
                    )
                }
            }
        }
    }

    private fun applyHomeCategory(category: HomeCategoryShortcut) {
        updateState { state ->
            val isSameSelection = state.selectedGroup == category.group && state.selectedCategory == category.category
            val selectedGroup = if (isSameSelection) null else category.group
            val selectedCategory = if (isSameSelection) null else category.category
            state.copy(
                selectedGroup = selectedGroup,
                selectedCategory = selectedCategory,
                isFilterApplied = false,
                venues = state.allVenues
                    .filteredBy(
                        group = selectedGroup,
                        category = selectedCategory
                    )
                    .sortByDistanceIfPossible(state.effectiveLocation())
            )
        }
        loadVenues()
    }

    private fun loadVenues() {
        searchJob?.cancel()

        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            request = { loadPublicVenues(currentState.searchQuery) },
            onError = { _, message ->
                alertController.show(
                    title = UiText.StringResource(DesignRes.string.error_alert_title),
                    message = message?.let(UiText::DynamicString)
                        ?: UiText.StringResource(Res.string.client_home_load_error_title),
                    type = NotificationType.ERROR
                )
            },
            onSuccess = { venues ->
                updateState { state ->
                    val selectedVenue = state.allVenues.firstOrNull { it.id == state.selectedVenueId }
                    val nextAllVenues = (venues + listOfNotNull(selectedVenue)).distinctBy(VenueResponse::id)
                    state.copy(
                        allVenues = nextAllVenues,
                        venues = nextAllVenues
                            .filteredBy(
                                group = state.selectedGroup,
                                category = state.selectedCategory
                            )
                            .sortByDistanceIfPossible(state.effectiveLocation())
                    )
                }
            }
        )
    }

    private fun openVenueDetails(venueId: String) {
        updateState {
            it.copy(
                currentScreen = ClientHomeScreen.VenueDetails,
                selectedVenueId = venueId,
                selectedReviewPhotoUrl = null
            )
        }
        if (currentState.allVenues.none { it.id == venueId } && currentState.venues.none { it.id == venueId }) {
            loadVenueById(venueId)
        }
    }

    private fun loadVenueById(venueId: String) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isLoading = loading) } },
            request = { getVenueByIdUseCase.execute(venueId) },
            onError = { _, message ->
                alertController.show(
                    title = UiText.StringResource(DesignRes.string.error_alert_title),
                    message = message?.let(UiText::DynamicString)
                        ?: UiText.StringResource(Res.string.client_home_load_error_title),
                    type = NotificationType.ERROR
                )
            },
            onSuccess = { venue ->
                updateState { state ->
                    state.copy(
                        allVenues = (listOf(venue) + state.allVenues).distinctBy(VenueResponse::id),
                        venues = (listOf(venue) + state.venues).distinctBy(VenueResponse::id)
                    )
                }
            }
        )
    }

    private fun refreshLocationAndLoadVenues() {
        viewModelScope.launch {
            val location = locationProvider.getCurrentLocation()
            val resolvedLocation = location?.let { gpsLocation ->
                when (val result = reverseLocationUseCase.execute(gpsLocation)) {
                    is DomainResult.Success -> result.data
                    is DomainResult.Error -> null
                }
            }
            updateState {
                it.copy(
                    userLocation = location,
                    currentLocationTitle = it.selectedSessionLocation?.title
                        ?: resolvedLocation?.title
                        ?: it.currentLocationTitle
                )
            }
            loadVenues()
        }
    }

    private fun searchLocations(query: String) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isLocationSearchLoading = loading) } },
            request = {
                searchLocationsUseCase.execute(
                    SearchClientLocationsCommand(
                        query = query,
                        proximity = currentState.effectiveLocation()
                    )
                )
            },
            onError = { _, _ ->
                updateState {
                    it.copy(
                        locationSuggestions = emptyList(),
                        isLocationSearchLoading = false
                    )
                }
            },
            onSuccess = { suggestions ->
                updateState {
                    it.copy(
                        locationSuggestions = suggestions,
                        isLocationSearchLoading = false
                    )
                }
            }
        )
    }

    private suspend fun loadPublicVenues(query: String): DomainResult<List<VenueResponse>> {
        val connected = loadVenuesByStatus(VenueStatus.CONNECTED, query)
        val addedByModerator = loadVenuesByStatus(VenueStatus.ADDED_BY_MODERATOR, query)

        val venues = buildList {
            if (connected is DomainResult.Success) addAll(connected.data)
            if (addedByModerator is DomainResult.Success) addAll(addedByModerator.data)
        }

        if (venues.isNotEmpty() || connected is DomainResult.Success || addedByModerator is DomainResult.Success) {
            return DomainResult.Success(
                venues
                    .distinctBy(VenueResponse::id)
                    .sortByDistanceIfPossible(currentState.effectiveLocation())
            )
        }

        return connected as DomainResult.Error
    }

    private suspend fun loadVenuesByStatus(
        status: VenueStatus,
        query: String
    ): DomainResult<List<VenueResponse>> {
        val response = getClientVenuesUseCase.execute(
            currentState.let { state ->
                VenueFilterRequest(
                    page = 1,
                    pageSize = PageSize,
                    query = query.trim().ifBlank { null },
                    status = status,
                    category = state.selectedCategory,
                    categoryGroup = when {
                        state.selectedCategory != null -> null
                        else -> state.selectedGroup
                    },
                    latitude = state.effectiveLocation()?.latitude,
                    longitude = state.effectiveLocation()?.longitude,
                    minDistanceKm = state.minDistanceFilterKm(),
                    maxDistanceKm = state.maxDistanceFilterKm(),
                    serviceQueries = state.selectedServiceQueries.takeIf { state.isFilterApplied }.orEmpty(),
                    minServicePrice = state.filterPriceRange.first.toDouble()
                        .takeIf { state.isFilterApplied && state.isPriceFilterChanged },
                    maxServicePrice = state.filterPriceRange.last.toDouble()
                        .takeIf { state.isFilterApplied && state.isPriceFilterChanged }
                )
            }
        )

        return when (response) {
            is DomainResult.Success -> DomainResult.Success(response.data.items)
            is DomainResult.Error -> response
        }
    }

    private fun loadServiceSuggestions(query: String) {
        launchNetwork(
            request = { getServiceSuggestionsUseCase.execute(query.trim()) },
            onError = { _, _ -> updateState { it.copy(serviceSuggestions = emptyList()) } },
            onSuccess = { options ->
                updateState { state ->
                    val selectedServices = state.selectedServiceQueries.map { it.lowercase() }.toSet()
                    val priceBounds = options.priceBounds() ?: state.filterPriceBounds
                    val priceRange = when {
                        state.isPriceFilterChanged -> state.filterPriceRange.coerceToBounds(priceBounds)
                        else -> priceBounds
                    }

                    state.copy(
                        serviceSuggestions = options.items
                            .take(ServiceSuggestionLimit)
                            .distinctBy { it.lowercase() }
                            .filterNot { it.lowercase() in selectedServices },
                        filterPriceBounds = priceBounds,
                        filterPriceRange = priceRange
                    )
                }
            }
        )
    }

    private fun submitReview() {
        val venueId = currentState.selectedVenueId ?: return
        val form = currentState.reviewForm
        if (form.overallRating !in MinReviewRating..MaxReviewRating) return

        requireClientSession {
            submitReviewAuthorized(venueId, form)
        }
    }

    private fun submitReviewAuthorized(
        venueId: String,
        form: ClientReviewFormState
    ) {
        launchNetwork(
            onLoading = { loading ->
                updateState { state ->
                    state.copy(reviewForm = state.reviewForm.copy(isSubmitting = loading))
                }
            },
            request = {
                createReviewUseCase.execute(
                    CreateClientReviewCommand(
                        venueId = venueId,
                        rating = form.overallRating,
                        details = form.toRatingDetails(),
                        text = form.text,
                        photos = form.photos.map {
                            ReviewMediaRef(
                                mediaId = it.mediaId,
                                publicUrl = it.publicUrl
                            )
                        }
                    )
                )
            },
            onError = { _, message ->
                alertController.show(
                    title = UiText.StringResource(DesignRes.string.error_alert_title),
                    message = message?.let(UiText::DynamicString)
                        ?: UiText.StringResource(Res.string.client_review_submit_error),
                    type = NotificationType.ERROR
                )
            },
            onSuccess = {
                alertController.show(
                    title = UiText.StringResource(Res.string.client_review_submit_success),
                    message = UiText.StringResource(Res.string.client_review_submit_success_message),
                    type = NotificationType.SUCCESS
                )
                updateState {
                    it.copy(
                        currentScreen = ClientHomeScreen.VenueDetails,
                        reviewForm = ClientReviewFormState()
                    )
                }
            }
        )
    }

    private fun loadVenueReviews(venueId: String) {
        launchNetwork(
            onLoading = { loading -> updateState { it.copy(isVenueReviewsLoading = loading) } },
            request = {
                getVenueReviewsUseCase.execute(
                    GetClientVenueReviewsCommand(
                        venueId = venueId,
                        page = 1,
                        pageSize = ReviewPageSize
                    )
                )
            },
            onError = { _, message ->
                alertController.show(
                    title = UiText.StringResource(DesignRes.string.error_alert_title),
                    message = message?.let(UiText::DynamicString)
                        ?: UiText.StringResource(Res.string.client_home_load_error_title),
                    type = NotificationType.ERROR
                )
            },
            onSuccess = { response ->
                updateState { it.copy(venueReviews = response.items) }
            }
        )

        launchNetwork(
            request = { getReviewSummaryUseCase.execute(venueId) },
            onError = { _, _ -> updateState { it.copy(venueReviewSummary = null) } },
            onSuccess = { summary ->
                updateState { it.copy(venueReviewSummary = summary) }
            }
        )
    }

    private fun toggleReviewLike(reviewId: String) {
        val review = currentState.venueReviews.firstOrNull { it.id == reviewId } ?: return

        requireClientSession {
            launchNetwork(
                onLoading = { loading ->
                    updateState { state ->
                        state.copy(
                            likingReviewIds = if (loading) {
                                state.likingReviewIds + reviewId
                            } else {
                                state.likingReviewIds - reviewId
                            }
                        )
                    }
                },
                request = {
                    toggleReviewLikeUseCase.execute(
                        ToggleClientReviewLikeCommand(
                            reviewId = review.id,
                            isLiked = review.isLikedByMe
                        )
                    )
                },
                onError = { _, message ->
                    alertController.show(
                        title = UiText.StringResource(DesignRes.string.error_alert_title),
                        message = message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_home_load_error_title),
                        type = NotificationType.ERROR
                    )
                },
                onSuccess = { updated ->
                    updateState { state ->
                        state.copy(
                            venueReviews = state.venueReviews.replaceReview(updated)
                        )
                    }
                }
            )
        }
    }

    private fun requireClientSession(onAllowed: () -> Unit) {
        viewModelScope.launch {
            val hasToken = !tokenStorage.getAccessToken().isNullOrBlank()
            if (hasToken) {
                onAllowed()
            } else {
                alertController.show(
                    title = UiText.StringResource(Res.string.client_review_auth_required_title),
                    message = UiText.StringResource(Res.string.client_review_auth_required_message),
                    type = NotificationType.WARNING
                )
            }
        }
    }

    private fun uploadReviewPhoto(file: ClientPickedFile) {
        val form = currentState.reviewForm
        if (form.photos.size >= MaxReviewPhotoCount) return

        val mimeType = file.mimeType.ifBlank { "image/jpeg" }
        if (!mimeType.startsWith("image/")) {
            alertController.show(
                title = UiText.StringResource(DesignRes.string.error_alert_title),
                message = UiText.StringResource(Res.string.client_review_photo_upload_error),
                type = NotificationType.ERROR
            )
            return
        }

        launchNetwork(
            onLoading = { loading ->
                updateState { state ->
                    state.copy(reviewForm = state.reviewForm.copy(isPhotoUploading = loading))
                }
            },
            request = {
                uploadImageUseCase.execute(
                    UploadImageCommand(
                        file = file.fileSource,
                        type = ImageType.COMMENTS,
                        mimeType = mimeType
                    )
                )
            },
            onError = { _, message ->
                alertController.show(
                    title = UiText.StringResource(DesignRes.string.error_alert_title),
                    message = message?.let(UiText::DynamicString)
                        ?: UiText.StringResource(Res.string.client_review_photo_upload_error),
                    type = NotificationType.ERROR
                )
            },
            onSuccess = { registration ->
                updateState { state ->
                    val exists = state.reviewForm.photos.any { it.mediaId == registration.mediaId }
                    val photos = if (exists) {
                        state.reviewForm.photos
                    } else {
                        state.reviewForm.photos + ClientReviewPhoto(
                            mediaId = registration.mediaId,
                            publicUrl = registration.publicUrl
                        )
                    }
                    state.copy(reviewForm = state.reviewForm.copy(photos = photos.take(MaxReviewPhotoCount)))
                }
            }
        )
    }

    private fun ClientHomeViewState.minDistanceFilterKm(): Double? {
        return when {
            effectiveLocation() == null || !isFilterApplied -> null
            filterLocation == ClientVenueLocationFilter.Over40Km -> LocationFilterThresholdKm
            else -> null
        }
    }

    private fun ClientHomeViewState.maxDistanceFilterKm(): Double? {
        return when {
            effectiveLocation() == null || !isFilterApplied -> null
            filterLocation == ClientVenueLocationFilter.Within40Km -> LocationFilterThresholdKm
            else -> null
        }
    }

    private fun ClientHomeViewState.effectiveLocation(): ClientGeoLocation? {
        return selectedSessionLocation?.toGeoLocation() ?: userLocation
    }

    private fun com.patronat.top.dto.VenueServiceSuggestionsResponse.priceBounds(): IntRange? {
        val min = minPrice?.let { floor(it).toInt() } ?: return null
        val max = maxPrice?.let { ceil(it).toInt() } ?: return null
        return min..max
    }

    private fun IntRange.coerceToBounds(bounds: IntRange): IntRange {
        if (bounds.first >= bounds.last) {
            return bounds
        }

        val start = first.coerceIn(bounds.first, bounds.last - 1)
        val end = last.coerceIn(start + 1, bounds.last)
        return start..end
    }

    private fun List<VenueResponse>.filteredBy(
        group: VenueGroup?,
        category: VenueCategory?
    ): List<VenueResponse> {
        return when {
            category != null -> filter { it.category == category }
            group != null -> filter { it.category.group == group }
            else -> this
        }
    }

    private fun List<VenueResponse>.sortByDistanceIfPossible(location: ClientGeoLocation?): List<VenueResponse> {
        if (location == null) {
            return sortedByDescending { it.updatedAt.toEpochMilliseconds() }
        }

        return sortedWith(
            compareBy<VenueResponse> { venue ->
                venue.distanceTo(location) ?: Double.POSITIVE_INFINITY
            }.thenByDescending { it.updatedAt.toEpochMilliseconds() }
        )
    }

    private fun List<ReviewResponse>.replaceReview(updated: ReviewResponse): List<ReviewResponse> {
        return map { review ->
            if (review.id == updated.id) updated else review
        }
    }

    private fun VenueResponse.distanceTo(location: ClientGeoLocation): Double? {
        val coordinates = this.location.coordinates ?: return null
        return haversineDistanceKm(
            latitude1 = location.latitude,
            longitude1 = location.longitude,
            latitude2 = coordinates.latitude,
            longitude2 = coordinates.longitude
        )
    }

    private fun haversineDistanceKm(
        latitude1: Double,
        longitude1: Double,
        latitude2: Double,
        longitude2: Double
    ): Double {
        val latitudeDelta = (latitude2 - latitude1).toRadians()
        val longitudeDelta = (longitude2 - longitude1).toRadians()
        val startLatitude = latitude1.toRadians()
        val endLatitude = latitude2.toRadians()
        val a = sin(latitudeDelta / 2).pow(2) +
            cos(startLatitude) * cos(endLatitude) * sin(longitudeDelta / 2).pow(2)

        return EarthRadiusKm * 2 * atan2(sqrt(a), sqrt(1 - a))
    }

    private fun Double.toRadians(): Double = this * PI / 180.0

    private fun ClientReviewFormState.withMetric(
        metric: ClientReviewMetric,
        value: Int?
    ): ClientReviewFormState {
        val nextValue = value?.coerceIn(MinReviewRating, MaxReviewRating)
        val updated = when (metric) {
            ClientReviewMetric.Price -> copy(priceRating = nextValue)
            ClientReviewMetric.Quality -> copy(qualityRating = nextValue)
            ClientReviewMetric.Speed -> copy(speedRating = nextValue)
            ClientReviewMetric.Staff -> copy(staffRating = nextValue)
            ClientReviewMetric.Atmosphere -> copy(atmosphereRating = nextValue)
        }
        return updated.copy(overallRating = updated.averageDetailRating())
    }

    private fun ClientReviewFormState.averageDetailRating(): Int {
        val values = listOfNotNull(
            priceRating,
            qualityRating,
            speedRating,
            staffRating,
            atmosphereRating
        )

        if (values.isEmpty()) return 0

        return values
            .average()
            .roundToInt()
            .coerceIn(MinReviewRating, MaxReviewRating)
    }

    private fun ClientReviewFormState.toRatingDetails(): ReviewRatingDetails? {
        if (
            priceRating == null &&
            qualityRating == null &&
            speedRating == null &&
            staffRating == null &&
            atmosphereRating == null
        ) {
            return null
        }

        return ReviewRatingDetails(
            price = priceRating,
            quality = qualityRating,
            speed = speedRating,
            staff = staffRating,
            atmosphere = atmosphereRating
        )
    }

    private companion object {
        const val PageSize = 200
        const val ReviewPageSize = 100
        const val SearchDebounceMillis = 350L
        const val LocationSearchMinLength = 2
        const val LocationFilterThresholdKm = 40.0
        const val EarthRadiusKm = 6371.0
        const val ServiceSuggestionLimit = 20
        const val MinReviewRating = 1
        const val MaxReviewRating = 10
        const val MaxReviewTextLength = 500
        const val MaxReviewPhotoCount = 5
    }
}
